<template>
  <div class="faq-editor mt-8">
    <v-divider class="v-divider-entity mt-5 mb-5"></v-divider>

    <div class="faq-editor__section-header">
      <h4>FAQ manuelle</h4>
      <v-btn
        small
        color="primary"
        @click="addManualBlock"
      >
        <v-icon
          left
          small
        >
          {{ localIcons.mdiPlus }}
        </v-icon>
        Ajouter un bloc
      </v-btn>
    </div>

    <v-alert
      v-if="manualBlocks.length === 0"
      text
      dense
      type="info"
      class="mt-4"
    >
      Aucun bloc FAQ manuelle.
    </v-alert>

    <Draggable
      :list="manualBlocks"
      handle=".faq-editor__drag"
      ghost-class="ghost"
      @end="syncPositions"
    >
      <v-card
        v-for="(block, blockIndex) in manualBlocks"
        :key="block.localKey"
        outlined
        class="faq-editor__block mt-4"
      >
        <v-card-title class="faq-editor__block-title">
          <v-icon
            class="faq-editor__drag mr-2"
            :title="'Déplacer le bloc'"
          >
            {{ localIcons.mdiDragVertical }}
          </v-icon>
          <span>Bloc {{ blockIndex + 1 }}</span>
          <v-spacer></v-spacer>
          <v-btn
            icon
            small
            :disabled="blockIndex === 0"
            :title="'Monter le bloc'"
            @click="moveManualBlock(blockIndex, -1)"
          >
            <v-icon small>
              {{ localIcons.mdiArrowUp }}
            </v-icon>
          </v-btn>
          <v-btn
            icon
            small
            :disabled="blockIndex === manualBlocks.length - 1"
            :title="'Descendre le bloc'"
            @click="moveManualBlock(blockIndex, 1)"
          >
            <v-icon small>
              {{ localIcons.mdiArrowDown }}
            </v-icon>
          </v-btn>
          <v-btn
            icon
            small
            color="error"
            :title="'Supprimer le bloc'"
            @click="removeManualBlock(blockIndex)"
          >
            <v-icon small>
              {{ localIcons.mdiDelete }}
            </v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-text-field
            v-model="block.title.fr"
            label="Titre du bloc"
            class="mb-5"
            outlined
            dense
            placeholder="Titre du bloc"
            hide-details
          >
            <template v-slot:append>
              <country-flag
                class="inner-input-flag"
                country="fr"
                size="normal"
                shadow
              />
            </template>
          </v-text-field>

          <div class="faq-editor__entry-header">
            <h4>Questions / Réponses</h4>
            <v-btn
              x-small
              outlined
              color="primary"
              @click="addEntry(block)"
            >
              <v-icon
                left
                small
              >
                {{ localIcons.mdiPlus }}
              </v-icon>
              Ajouter une Q/R
            </v-btn>
          </div>

          <v-alert
            v-if="block.entries.length === 0"
            text
            dense
            type="info"
            class="mt-4"
          >
            Aucune question / réponse.
          </v-alert>

          <Draggable
            :list="block.entries"
            handle=".faq-editor__drag-entry"
            ghost-class="ghost"
            @end="syncPositions"
          >
            <div
              v-for="(entry, entryIndex) in block.entries"
              :key="entry.localKey"
              class="faq-editor__entry mt-4"
            >
              <div class="faq-editor__entry-toolbar">
                <div>
                  <v-icon
                    class="faq-editor__drag-entry mr-2"
                    :title="'Déplacer la Q/R'"
                  >
                    {{ localIcons.mdiDragVertical }}
                  </v-icon>
                  <span>Q/R {{ entryIndex + 1 }}</span>
                </div>
                <div>
                  <v-btn
                    icon
                    small
                    :disabled="entryIndex === 0"
                    :title="'Monter la Q/R'"
                    @click="moveEntry(block, entryIndex, -1)"
                  >
                    <v-icon small>
                      {{ localIcons.mdiArrowUp }}
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    :disabled="entryIndex === block.entries.length - 1"
                    :title="'Descendre la Q/R'"
                    @click="moveEntry(block, entryIndex, 1)"
                  >
                    <v-icon small>
                      {{ localIcons.mdiArrowDown }}
                    </v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="error"
                    :title="'Supprimer la Q/R'"
                    @click="removeEntry(block, entryIndex)"
                  >
                    <v-icon small>
                      {{ localIcons.mdiDelete }}
                    </v-icon>
                  </v-btn>
                </div>
              </div>

              <v-text-field
                v-model="entry.question.fr"
                label="Question"
                class="mb-5"
                outlined
                dense
                placeholder="Question"
                :rules="[questionRequiredRule]"
              >
                <template v-slot:append>
                  <country-flag
                    class="inner-input-flag"
                    country="fr"
                    size="normal"
                    shadow
                  />
                </template>
              </v-text-field>

              <div class="content-ckeditor-region faq-editor__answer">
                <h4>Réponse</h4>
                <br>
                <ckeditor
                  v-model="entry.answer.fr"
                  :editor="editor"
                  :config="editorConfig"
                  height="10"
                  row="50"
                />
                <country-flag
                  class="inner-input-flag content-flag-placeholder"
                  country="fr"
                  size="normal"
                  shadow
                />
              </div>
            </div>
          </Draggable>
        </v-card-text>
      </v-card>
    </Draggable>
  </div>
</template>

<script>
import Vue from 'vue'
import Draggable from 'vuedraggable'
import CKEditor from '@ckeditor/ckeditor5-vue2'
import {
  mdiArrowDown,
  mdiArrowUp,
  mdiDelete,
  mdiDragVertical,
  mdiPlus
} from '@mdi/js'

export default {
  name: 'FaqEditor',
  components: {
    Draggable,
    ckeditor: CKEditor.component
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    editor: {
      type: [Function, Object],
      default: null
    },
    editorConfig: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  data () {
    return {
      locales: ['fr'],
      manualBlocks: [],
      localIcons: {
        mdiArrowDown,
        mdiArrowUp,
        mdiDelete,
        mdiDragVertical,
        mdiPlus
      },
      questionRequiredRule: value => !!value || 'Question obligatoire'
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (blocks) {
        this.hydrateBlocks(blocks || [])
      }
    }
  },
  methods: {
    hydrateBlocks (blocks) {
      const sortedBlocks = [...blocks].sort((firstBlock, secondBlock) => {
        return this.getPosition(firstBlock) - this.getPosition(secondBlock)
      })

      this.manualBlocks = []

      for (let i = 0; i < sortedBlocks.length; i++) {
        const currentRawBlock = sortedBlocks[i]

        if (currentRawBlock.source === 'imported') {
          continue
        }

        this.manualBlocks.push(this.normalizeBlock(currentRawBlock, this.manualBlocks.length))
      }
    },
    normalizeBlock (block, blockIndex) {
      const normalizedBlock = {
        id: block.id || null,
        localKey: block.id || this.generateUniqueId(),
        source: block.source || 'manual',
        position: block.position !== undefined ? block.position : blockIndex,
        title: this.normalizeTranslations(block, ['title']).title,
        entries: []
      }

      const entries = block.entries || []
      const sortedEntries = [...entries].sort((firstEntry, secondEntry) => {
        return this.getPosition(firstEntry) - this.getPosition(secondEntry)
      })

      for (let i = 0; i < sortedEntries.length; i++) {
        normalizedBlock.entries.push(this.normalizeEntry(sortedEntries[i], i))
      }

      return normalizedBlock
    },
    normalizeEntry (entry, entryIndex) {
      const translations = this.normalizeTranslations(entry, ['question', 'answer'])

      return {
        id: entry.id || null,
        localKey: entry.id || this.generateUniqueId(),
        position: entry.position !== undefined ? entry.position : entryIndex,
        question: translations.question,
        answer: translations.answer
      }
    },
    normalizeTranslations (data, fields) {
      const normalized = {}

      for (let i = 0; i < fields.length; i++) {
        const field = fields[i]

        normalized[field] = {}

        for (let j = 0; j < this.locales.length; j++) {
          const locale = this.locales[j]

          Vue.set(normalized[field], locale, this.getTranslatedValue(data, field, locale))
        }
      }

      return normalized
    },
    getTranslatedValue (data, field, locale) {
      if (data.translations && data.translations[locale] && data.translations[locale][field] !== undefined) {
        return data.translations[locale][field] || ''
      }

      if (data.newTranslations && data.newTranslations[locale] && data.newTranslations[locale][field] !== undefined) {
        return data.newTranslations[locale][field] || ''
      }

      if (locale === 'fr' && data[field] !== undefined) {
        return data[field] || ''
      }

      return ''
    },
    getPosition (item) {
      if (!item || item.position === undefined || item.position === null) {
        return 0
      }

      return item.position
    },
    addManualBlock () {
      this.manualBlocks.push({
        id: null,
        localKey: this.generateUniqueId(),
        source: 'manual',
        position: this.manualBlocks.length,
        title: this.createLocaleMap(''),
        entries: []
      })

      this.syncPositions()
    },
    removeManualBlock (blockIndex) {
      this.manualBlocks.splice(blockIndex, 1)
      this.syncPositions()
    },
    moveManualBlock (blockIndex, direction) {
      const newIndex = blockIndex + direction

      if (newIndex < 0 || newIndex >= this.manualBlocks.length) {
        return
      }

      const block = this.manualBlocks.splice(blockIndex, 1)[0]

      this.manualBlocks.splice(newIndex, 0, block)
      this.syncPositions()
    },
    addEntry (block) {
      block.entries.push({
        id: null,
        localKey: this.generateUniqueId(),
        position: block.entries.length,
        question: this.createLocaleMap(''),
        answer: this.createLocaleMap('')
      })

      this.syncPositions()
    },
    removeEntry (block, entryIndex) {
      block.entries.splice(entryIndex, 1)
      this.syncPositions()
    },
    moveEntry (block, entryIndex, direction) {
      const newIndex = entryIndex + direction

      if (newIndex < 0 || newIndex >= block.entries.length) {
        return
      }

      const entry = block.entries.splice(entryIndex, 1)[0]

      block.entries.splice(newIndex, 0, entry)
      this.syncPositions()
    },
    createLocaleMap (defaultValue) {
      const localeMap = {}

      for (let i = 0; i < this.locales.length; i++) {
        Vue.set(localeMap, this.locales[i], defaultValue)
      }

      return localeMap
    },
    syncPositions () {
      for (let i = 0; i < this.manualBlocks.length; i++) {
        this.manualBlocks[i].position = i

        for (let j = 0; j < this.manualBlocks[i].entries.length; j++) {
          this.manualBlocks[i].entries[j].position = j
        }
      }
    },
    serializeFaqBlocks () {
      const payload = []

      for (let i = 0; i < this.manualBlocks.length; i++) {
        payload.push(this.serializeBlock(this.manualBlocks[i], i))
      }

      return this.cloneFaqBlocks(payload)
    },
    serializeBlock (block, position) {
      const serializedBlock = {
        source: 'manual',
        position,
        entries: [],
        newTranslations: this.buildTranslations(block, ['title'])
      }

      if (block.id) {
        serializedBlock.id = block.id
      }

      for (let i = 0; i < block.entries.length; i++) {
        serializedBlock.entries.push(this.serializeEntry(block.entries[i], i))
      }

      return serializedBlock
    },
    serializeEntry (entry, position) {
      const serializedEntry = {
        position,
        newTranslations: this.buildTranslations(entry, ['question', 'answer'])
      }

      if (entry.id) {
        serializedEntry.id = entry.id
      }

      return serializedEntry
    },
    buildTranslations (item, fields) {
      const translations = {}

      for (let i = 0; i < this.locales.length; i++) {
        const locale = this.locales[i]
        const translation = {}

        for (let j = 0; j < fields.length; j++) {
          const field = fields[j]

          translation[field] = item[field] && item[field][locale] ? item[field][locale] : ''
        }

        translations[locale] = translation
      }

      return translations
    },
    cloneFaqBlocks (blocks) {
      return JSON.parse(JSON.stringify(blocks))
    }
  }
}
</script>

<style scoped>
.faq-editor__section-header,
.faq-editor__entry-header,
.faq-editor__entry-toolbar {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.faq-editor__block-title {
  font-size: 1rem;
  padding-bottom: 8px;
  padding-top: 8px;
}

.faq-editor__drag,
.faq-editor__drag-entry {
  cursor: move;
}

.faq-editor__entry {
  border: 1px solid rgba(94, 86, 105, 0.18);
  border-radius: 4px;
  padding: 16px;
}

.faq-editor__entry-toolbar {
  margin-bottom: 16px;
}

.faq-editor__answer {
  margin-top: 8px;
}
</style>
